import { collection, addDoc, Timestamp, query, where, orderBy, getDocs, updateDoc, doc, getDoc, DocumentReference } from 'firebase/firestore';
import { db } from '../config/firebase';

type NotificationType = 'success' | 'warning' | 'error' | 'info';

interface Notification {
  id: string;
  companyId: string;
  title: string;
  content: string;
  type: NotificationType;
  link?: string;
  timestamp: Timestamp;
  read: boolean;
  createdAt: string;
  status: string;
}

// Função para verificar se um documento existe
async function doesDocumentExist(docRef: DocumentReference): Promise<boolean> {
  const doc = await getDoc(docRef);
  return doc.exists();
}

export async function createNotification(
  companyId: string,
  notification: {
    title: string,
    content: string,
    type?: NotificationType,
    link?: string
  }
) {
  try {
    if (!companyId) {
      console.error('Cannot create notification: companyId is required');
      return;
    }

    console.log(`Creating notification for company ${companyId}:`, notification);

    const notificationRef = collection(db, 'notifications');
    const newNotification = {
      companyId,
      title: notification.title,
      content: notification.content,
      type: notification.type || 'info',
      link: notification.link,
      timestamp: Timestamp.now(),
      read: false,
      createdAt: new Date().toISOString(),
      status: 'active'
    };

    const docRef = await addDoc(notificationRef, newNotification);
    console.log('Notification created with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
}

export async function checkNewMessages() {
  try {
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('read', '==', false),
      where('notified', '==', false),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    
    for (const doc of snapshot.docs) {
      const message = doc.data();
      
      // Create notification for unread message
      await createNotification(
        message.recipientId,
        {
          title: 'Nova Mensagem',
          content: `Você recebeu uma nova mensagem de ${message.sender}`,
          type: 'info',
          link: `/messages/${doc.id}`
        }
      );

      // Mark message as notified
      await updateDoc(doc.ref, { notified: true });
    }
  } catch (error) {
    console.error('Error checking new messages:', error);
  }
}

export async function checkNewOrders() {
  try {
    console.log('Starting checkNewOrders at:', new Date().toISOString());
    const ordersRef = collection(db, 'orders');
    
    // Get orders that are pending and haven't been notified
    const q = query(
      ordersRef,
      where('status', '==', 'pending'),
      where('notified', '==', false),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    console.log(`Found ${snapshot.docs.length} unnotified pending orders`);
    
    for (const doc of snapshot.docs) {
      try {
        const order = { ...doc.data(), id: doc.id };
        console.log('Processing order:', {
          id: order.id,
          createdAt: order.createdAt,
          isOnlineSale: order.isOnlineSale,
          items: order.items?.length,
          notified: order.notified,
          status: order.status
        });

        // Convert createdAt to Date object
        let orderDate: Date;
        if (typeof order.createdAt === 'string') {
          orderDate = new Date(order.createdAt);
        } else if (order.createdAt?.toDate) {
          orderDate = order.createdAt.toDate();
        } else {
          console.error(`Invalid createdAt format for order ${order.id}:`, order.createdAt);
          continue;
        }

        // Check if it's a marketplace order and has items
        if (order.isOnlineSale && order.items?.length > 0) {
          console.log(`Order ${order.id} items:`, JSON.stringify(order.items, null, 2));
          
          let notificationsSent = false;
          
          // Process each item individually
          for (const item of order.items) {
            // Log the item structure
            console.log(`Processing item:`, JSON.stringify(item, null, 2));
            
            const companyId = item.companyId || (item.product && item.product.companyId);
            
            if (companyId) {
              console.log(`Found companyId ${companyId} for item in order ${order.id}`);
              
              try {
                await createNotification(
                  companyId,
                  {
                    title: 'Novo Pedido Pendente',
                    content: `Você tem um novo pedido pendente (#${order.id})`,
                    type: 'warning',
                    link: `/marketplace/orders/${order.id}`
                  }
                );
                notificationsSent = true;
              } catch (error) {
                console.error(`Error creating notification for company ${companyId}:`, error);
              }
            } else {
              console.warn(`No companyId found for item in order ${order.id}`);
            }
          }

          // Mark order as notified only if at least one notification was sent
          if (notificationsSent) {
            console.log(`Marking order ${order.id} as notified`);
            await updateDoc(doc.ref, { notified: true });
            console.log(`Successfully marked order ${order.id} as notified`);
          } else {
            console.error(`No notifications were sent for order ${order.id}`);
          }
        } else {
          console.log(`Skipping order ${order.id} - not a marketplace order or no items`);
        }
      } catch (error) {
        console.error(`Error processing order:`, error);
      }
    }
    console.log('Finished checking new orders at:', new Date().toISOString());
  } catch (error) {
    console.error('Error in checkNewOrders:', error);
  }
}

export async function getNotifications(companyId: string): Promise<Notification[]> {
  try {
    console.log(`Fetching notifications for company ${companyId}`);
    const notificationsRef = collection(db, 'notifications');
    
    // First, get all notifications for the company
    const q = query(
      notificationsRef,
      where('companyId', '==', companyId)
    );

    const snapshot = await getDocs(q);
    const notifications = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];

    // Filter and sort in memory to avoid index requirements
    const unreadNotifications = notifications
      .filter(notification => !notification.read)
      .sort((a, b) => b.timestamp.seconds - a.timestamp.seconds);

    console.log(`Found ${unreadNotifications.length} unread notifications`);
    return unreadNotifications;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
}

export async function markNotificationAsRead(notificationId: string) {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true,
      readAt: Timestamp.now()
    });
    console.log(`Marked notification ${notificationId} as read`);
  } catch (error) {
    console.error(`Error marking notification ${notificationId} as read:`, error);
    throw error;
  }
}
